/**
 * The chrome around every public screen.
 *
 * Landing, pricing and the four legal pages had — or would have grown — their
 * own copy of the same header and the same footer. That matters more than the
 * duplication usually would: the footer is where the terms, the refund policy
 * and the merchant of record are disclosed, and a page that quietly lacks those
 * links is a page a customer cannot get out of. One component means a screen
 * cannot be added without them.
 */

import type { ReactNode } from 'react';

import {
  COMPANY,
  LEGAL_EFFECTIVE_DATE,
  MERCHANT_OF_RECORD,
  SUPPORT_EMAIL,
  companyIsNamed,
  sellerName,
} from '../../core/company';
import { link } from '../router';
import { useTheme } from '../theme';
import { Button } from '../ui';
import { Icon, IconHover, MoonIcon, SunIcon, Wordmark } from '../icons';

export function SiteHeader({
  signedIn = false,
  showPricing = true,
}: {
  signedIn?: boolean;
  /** Off on the pricing page itself, where it would link to where you are. */
  showPricing?: boolean;
}) {
  const { isDark, setTheme } = useTheme();

  return (
    <header className="border-b border-line">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
        <a
          {...link(signedIn ? '/evidence' : '/')}
          className="flex items-center gap-2 font-medium tracking-tight"
        >
          <span className="text-accent">
            <Wordmark size={20} />
          </span>
          {COMPANY.product}
        </a>

        <div className="flex items-center gap-2">
          <IconHover
            as="button"
            className="btn btn-ghost"
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            <Icon icon={isDark ? SunIcon : MoonIcon} size={16} />
          </IconHover>

          {signedIn ? (
            <a {...link('/evidence')}>
              <Button variant="secondary">Back to app</Button>
            </a>
          ) : (
            <>
              {showPricing ? (
                <a {...link('/pricing')}>
                  <Button variant="ghost">Pricing</Button>
                </a>
              ) : null}
              <a {...link('/login')}>
                <Button variant="ghost">Sign in</Button>
              </a>
              <a {...link('/signup')}>
                <Button variant="primary">Start free</Button>
              </a>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function FooterLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <a {...link(to)} className="block text-faint transition-colors hover:text-ink">
      {children}
    </a>
  );
}

/**
 * The footer.
 *
 * Every link here is one somebody needs before they will type a card number:
 * what the terms are, what happens to their documents, how to get their money
 * back, and who to write to when it goes wrong. They are not there to satisfy a
 * checklist — a security team evaluating a vendor opens all four, and the
 * absence of any of them is itself an answer.
 *
 * The merchant of record line is a legal disclosure rather than a credit. Polar
 * is the seller on the transaction and the name on the statement, and a customer
 * who cannot reconcile "POLAR*VOUCHKEEP" against a subscription they remember
 * buying files a chargeback instead of an email.
 */
export function SiteFooter() {
  const year = new Date().getFullYear();

  return (
    <footer className="mt-auto border-t border-line">
      <div className="mx-auto max-w-5xl px-5 py-10">
        <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-2 text-[14px] font-medium tracking-tight">
              <span className="text-accent">
                <Wordmark size={17} />
              </span>
              {COMPANY.product}
            </div>
            <p className="mt-2 text-[12.5px] leading-relaxed text-faint">
              No answer ships without evidence.
            </p>
          </div>

          <div className="space-y-2 text-[12.5px]">
            <div className="font-medium text-muted">Product</div>
            <FooterLink to="/pricing">Pricing</FooterLink>
            <FooterLink to="/signup">Start free</FooterLink>
            <FooterLink to="/login">Sign in</FooterLink>
          </div>

          <div className="space-y-2 text-[12.5px]">
            <div className="font-medium text-muted">Legal</div>
            <FooterLink to="/terms">Terms of service</FooterLink>
            <FooterLink to="/privacy">Privacy policy</FooterLink>
            <FooterLink to="/refunds">Refunds and cancellation</FooterLink>
          </div>

          <div className="space-y-2 text-[12.5px]">
            <div className="font-medium text-muted">Support</div>
            <FooterLink to="/contact">Contact us</FooterLink>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="block text-faint transition-colors hover:text-ink"
            >
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>

        <div className="mt-9 space-y-2 border-t border-line pt-6 text-[12px] leading-relaxed text-faint">
          <p>
            © {year} {sellerName()}
            {companyIsNamed() && COMPANY.jurisdiction ? ` · ${COMPANY.jurisdiction}` : ''} · Terms
            effective {LEGAL_EFFECTIVE_DATE}
          </p>
          <p>
            Payments are processed by{' '}
            <a
              href={MERCHANT_OF_RECORD.url}
              target="_blank"
              rel="noreferrer"
              className="text-muted underline underline-offset-2 hover:text-ink"
            >
              {MERCHANT_OF_RECORD.name}
            </a>
            , our {MERCHANT_OF_RECORD.role}, who issues your invoice and handles applicable sales
            tax and VAT. Charges appear on your statement as{' '}
            <span className="text-muted">{MERCHANT_OF_RECORD.statementHint}</span>.
          </p>
        </div>
      </div>
    </footer>
  );
}
